import { action, computed, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { IGaugeRepository } from "@/domains/gauge"
import type { ILock, ILockRepository } from "@/domains/lock"
import { LcAddress } from "@/domains/core"
import type { ManagerStore } from "@/domains/manager/manager-store"

import type { LockItemDTO } from "../vote-page/components/cast-your-vote-card/types"
import type { ISelectedVotePowerProvider } from "./interfaces"
import { NftSelectionChangedEvent } from "./events"

/**
 * SelectedPowerStore - Pure MobX store for managing selected NFT power source
 *
 * This store handles all logic related to selecting NFTs and calculating vote power.
 * It's completely independent of VotePageStore, breaking circular dependencies.
 *
 * Pure computed properties - no reactions, no untracked - just MobX reactivity.
 */
export class SelectedPowerStore implements ISelectedVotePowerProvider {
  // Observable state
  selectedNftIds: number[] = []

  constructor(
    private readonly userLockRepository: ILockRepository,
    private readonly managerLockRepository: ILockRepository,
    private readonly managerStore: ManagerStore,
    private readonly eventBus: IEventBus,
    private readonly gaugeRepository: IGaugeRepository,
  ) {
    makeObservable(this, {
      selectedNftIds: observable,
      activeLockRepository: computed,
      activeLocks: computed,
      activeLocksAsDTOs: computed,
      allAvailableLocks: computed,
      activeLocksForDisplay: computed,
      inactiveLocksForDisplay: computed,
      inactiveLocksAsDTOs: computed,
      selectedLocksAsDTOs: computed,
      selectedVotePower: computed,
      setSelectedNfts: action,
      toggleNftSelection: action,
    })
  }

  /**
   * Get the active lock repository based on manager mode
   * Pure computed property - automatically tracks managerStore.isManagerMode
   */
  get activeLockRepository(): ILockRepository {
    return this.managerStore.isManagerMode ? this.managerLockRepository : this.userLockRepository
  }

  /**
   * Get all available locks from the repository (not filtered)
   * Pure computed property - automatically tracks activeLockRepository changes
   */
  get allAvailableLocks(): ILock[] {
    return this.activeLockRepository.getAll()
  }

  /**
   * Get all active locks (can vote - not expired, not voted this epoch)
   * These will be displayed at the top of the selection drawer
   * Pure computed property - automatically tracks activeLockRepository changes
   */
  get activeLocks(): ILock[] {
    const allLocks = this.allAvailableLocks
    return allLocks.filter((lock) => !lock.hasExpired && !lock.hasVotedThisEpoch)
  }

  /**
   * Get all active locks (can vote - not expired, not voted this epoch)
   * These will be displayed at the top of the selection drawer
   * Pure computed property - automatically tracks activeLockRepository changes
   */
  get activeLocksForDisplay(): ILock[] {
    const allLocks = this.allAvailableLocks
    return allLocks.filter((lock) => !lock.hasExpired && !lock.hasVotedThisEpoch)
  }

  /**
   * Get all inactive locks (cannot vote - expired or voted this epoch)
   * These will be displayed below active locks in a separate section
   * Pure computed property - automatically tracks activeLockRepository changes
   */
  get inactiveLocksForDisplay(): ILock[] {
    const allLocks = this.allAvailableLocks
    return allLocks.filter((lock) => lock.hasExpired || lock.hasVotedThisEpoch)
  }

  /**
   * Helper method to resolve gauge display name
   * Looks up gauge in repository and returns display name or shortened address
   */
  private getGaugeDisplayName(gaugeAddress: LcAddress): string {
    const gauge = this.gaugeRepository.find(gaugeAddress)
    if (gauge?.revenueSource?.displayName) {
      return gauge.revenueSource.displayName
    }
    // Fall back to shortened address (e.g., "0x1234...5678")
    const addressStr = gaugeAddress.toString()
    return `${addressStr.slice(0, 6)}...${addressStr.slice(-4)}`
  }

  /**
   * Helper method to convert lock to DTO with vote data
   */
  private lockToDTO(lock: ILock): LockItemDTO {
    return {
      id: lock.id,
      lockValue: lock.lockValue,
      lockAmount: lock.lockAmount,
      displayLockEnds: lock.displayLockEnds,
      hasVotedThisEpoch: lock.hasVotedThisEpoch,
      hasExpired: lock.hasExpired,
      lastEpochVotes: lock.lastEpochVotes.map((vote) => ({
        gaugeAddress: vote.gaugeAddress,
        votePercent: vote.votePercent,
        balance: vote.balance,
        gaugeDisplayName: this.getGaugeDisplayName(vote.gaugeAddress),
      })),
    }
  }

  /**
   * Get active locks as DTOs (can vote)
   * These will be displayed at the top
   * Pure computed property - breaks observable chain for UI components
   */
  get activeLocksAsDTOs(): LockItemDTO[] {
    return this.activeLocksForDisplay.map((lock) => this.lockToDTO(lock))
  }

  /**
   * Get inactive locks as DTOs (cannot vote - expired or voted)
   * These will be displayed below active locks
   * Pure computed property - breaks observable chain for UI components
   */
  get inactiveLocksAsDTOs(): LockItemDTO[] {
    return this.inactiveLocksForDisplay.map((lock) => this.lockToDTO(lock))
  }

  /**
   * Get selected locks as DTOs
   * Pure computed property - filters both active and inactive locks by selectedNftIds
   * Users can select from both active and inactive locks
   */
  get selectedLocksAsDTOs(): LockItemDTO[] {
    const selectedNftIds = this.selectedNftIds
    const allLocks = [...this.activeLocksAsDTOs, ...this.inactiveLocksAsDTOs]
    return allLocks.filter((lock) => selectedNftIds.includes(lock.id))
  }

  /**
   * Get total vote power from selected locks
   * Pure computed property - sums lockValue from selected locks
   */
  get selectedVotePower(): number {
    const selectedLocks = this.selectedLocksAsDTOs
    return selectedLocks.reduce((sum, lock) => sum + lock.lockValue, 0)
  }

  /**
   * Implementation of ISelectedVotePowerProvider interface
   */
  getSelectedVotePower(): number {
    return this.selectedVotePower
  }

  /**
   * Get selected NFT IDs
   */
  getSelectedNftIds(): number[] {
    return [...this.selectedNftIds]
  }

  /**
   * Set selected NFT IDs
   */
  setSelectedNfts(nftIds: number[]): void {
    this.selectedNftIds = nftIds
    this.publishSelectionChangedEvent()
  }

  /**
   * Toggle NFT selection
   */
  toggleNftSelection(nftId: number): void {
    if (this.selectedNftIds.includes(nftId)) {
      this.selectedNftIds = this.selectedNftIds.filter((id) => id !== nftId)
    } else {
      // Add and sort to maintain order
      const sortedIds = [...this.selectedNftIds, nftId].sort((a, b) => a - b)
      this.selectedNftIds = sortedIds
    }
    this.publishSelectionChangedEvent()
  }

  /**
   * Publish event when NFT selection changes
   * Includes complete state: all selected IDs and total vote power
   */
  private publishSelectionChangedEvent(): void {
    const selectedNftIds = [...this.selectedNftIds]
    const selectedVotePower = this.selectedVotePower
    this.eventBus.publish(new NftSelectionChangedEvent(selectedNftIds, selectedVotePower))
  }

  /**
   * Auto-select single NFT if there's exactly one active lock and nothing is selected
   * This is a computed property that returns the auto-selected ID, not a reaction
   * Components can check this and call setSelectedNfts if needed
   */
  get autoSelectedNftId(): number | null {
    const activeLocks = this.activeLocks
    if (activeLocks.length === 1 && this.selectedNftIds.length === 0) {
      return activeLocks[0]!.id
    }
    return null
  }
}
